import {ConfirmDialog} from "primereact/confirmdialog";
import {Dropdown, type DropdownProps} from "primereact/dropdown";
import {Toast} from "primereact/toast";
import {Tooltip} from "primereact/tooltip";
import {useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";

import {useAccessLostByReassignmentHandler} from "../../hooks/accessLostByReassignmentHooks";
import {useRevaTranslation} from "../../hooks/languageHooks";
import {useCheckPermission} from "../../hooks/permissionHooks";
import {selectFullName, selectUsername} from "../../redux/authSlice";
import {selectAssignableUsers, selectAssignableUsersLoading, selectLifeCycleState} from "../../redux/persistenceSlice";
import {assignValuation, loadAssignableUsers} from "../../redux/persistenceThunk";
import {useRevaDispatch} from "../../redux/revaHooks";
import {errorDetailFromErrorI18n} from "../../util/errorUtil";
import {
    isOwnershipRestrictedToFinalizers,
    PERMISSION_RULE_FINALIZATION,
    PERMISSION_RULE_OWNERSHIP_ADMIN,
    PERMISSION_RULE_OWNERSHIP_ASSIGN,
} from "../../util/permissions";

interface AssignUserDropdownProps {
    /** revaId of the valuation */
    revaId: string | null | undefined;
    /** currently assigned owner */
    currentOwner?: string | null;
    /** full name of the currently assigned owner */
    currentOwnerFullName?: string | null;
    /** CSS className for the Dropdown */
    className?: string;
}

/**
 * Assign User Dropdown for assigning a user to a valuation.
 */
export function AssignUserDropdown({revaId, currentOwner, currentOwnerFullName, className}: AssignUserDropdownProps) {
    const {t, language} = useRevaTranslation();
    const dispatch = useRevaDispatch();
    const toastRef = useRef<Toast>(null);
    const handleAccessLost = useAccessLostByReassignmentHandler();

    const ownUsername = useSelector(selectUsername);
    const ownFullName = useSelector(selectFullName);
    const assignableOwners = useSelector(selectAssignableUsers);
    const assignableOwnersLoading = useSelector(selectAssignableUsersLoading);
    // ADMINS ist bei beiden implizit enthalten (Regeln [ADMINS, …]) — konsistent mit dem Backend.
    const revaOwnershipAssign = useCheckPermission(PERMISSION_RULE_OWNERSHIP_ASSIGN);
    const revaOwnershipAdmin = useCheckPermission(PERMISSION_RULE_OWNERSHIP_ADMIN);
    const mayFinalize = useCheckPermission(PERMISSION_RULE_FINALIZATION);
    const lifeCycleState = useSelector(selectLifeCycleState);

    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [pendingUser, setPendingUser] = useState<string | null>(null);
    // Zuweisungs-Request läuft: Dropdown disabled + Spinner; optimisticOwner zeigt schon
    // den gewählten User an, während der Request läuft (konvergiert nach Erfolg via Redux).
    const [assigning, setAssigning] = useState(false);
    const [optimisticOwner, setOptimisticOwner] = useState<string | null>(null);

    const empty_owner = {label: t("order.owner.empty"), value: "empty_owner"};

    useEffect(() => {
        // Die Liste zuweisbarer (fremder) User wird nur mit reva-ownership-assign gebraucht — und der
        // Backend-Endpoint /assignableUsers verlangt dieses Recht ohnehin (sonst 403). Ohne das Recht
        // wird gar nicht geladen; es stehen dann nur Pool und die Selbst-Zuweisung zur Verfügung.
        // Bei einem Zustandswechsel verwirft persistenceSlice die Liste (sie hängt vom Zustand ab), dann lädt dieser
        // Effect sie neu.
        if (revaOwnershipAssign && !assignableOwners && !assignableOwnersLoading)
            void dispatch(loadAssignableUsers(revaId));
    }, [dispatch, revaId, revaOwnershipAssign, assignableOwners, assignableOwnersLoading]);

    if (!revaId) return null;

    const performAssignment = async (value: string) => {
        // Optimistisch den gewählten Wert anzeigen und das Control sperren, solange der
        // Request läuft. finally setzt beides zurück (auch im Fehlerfall – sonst bliebe das
        // Dropdown dauerhaft disabled). Bei Erfolg liefert Redux den neuen currentOwner nach.
        setAssigning(true);
        setOptimisticOwner(value);
        try {
            // empty_owner bedeutet "nicht zugewiesen" => null an die API, sonst der gewählte Username.
            // Auch das Entfernen des Owners kann accessLostByReassignment melden, daher EIN gemeinsamer
            // Pfad mit Response-Verarbeitung (kein fast-return mehr für empty_owner).
            const username = value === "empty_owner" ? null : value;
            const response = await dispatch(assignValuation(revaId, username));
            // #18343: meldet das Backend den Zugriffsverlust, Warn-Toast und Rücksprung zur Startseite
            handleAccessLost(response, toastRef.current);
        } catch (err) {
            console.error("Error assigning valuation", err);
            // Konkrete Backend-Meldung anzeigen, wenn vorhanden — sonst generisch.
            toastRef.current?.show({
                severity: "error",
                summary: "Error",
                detail: errorDetailFromErrorI18n(err, language) ?? t("general.error.unspecific"),
            });
        } finally {
            setAssigning(false);
            setOptimisticOwner(null);
        }
    };

    const onChange: NonNullable<DropdownProps["onChange"]> = (e) => {
        // der Nutzer weist einen Auftrag einem anderen Nutzer zu und kann diesen nicht mehr bearbeiten, weil Rolle revaOwnershipAdmin false ist
        if (!revaOwnershipAdmin && e.value !== "empty_owner" && ownUsername !== e.value) {
            setPendingUser(e.value);
            setShowConfirmDialog(true);
            return;
        }
        void performAssignment(e.value);
    };

    // Während die zuweisbaren Owner noch per AJAX geladen werden: ein deaktiviertes
    // Dummy-Dropdown mit dem (bereits feststehenden) aktuellen Owner und einem Spinner-Icon.
    // So bekommt der User sofort Feedback und das Control "springt" nicht erst nach dem
    // Laden plötzlich ins Layout. Die Mini-Options-Liste enthält nur den aktuellen Owner,
    // damit dessen Name korrekt angezeigt wird (sonst kann das Dropdown den Wert nicht auflösen).
    if (assignableOwnersLoading) {
        const loadingOptions = currentOwner ? [{label: currentOwnerFullName ?? currentOwner, value: currentOwner}] : [];
        return (
            <div className={"mb-0"}>
                <Dropdown
                    disabled
                    loading
                    className={className}
                    placeholder={t("order.owner.empty")}
                    value={currentOwner}
                    options={loadingOptions}
                />
            </div>
        );
    }

    // Diese Komponente wird von PanelRevaOrder nur für nicht-finalisierte Bewertungen
    // (lifeCycleState !== "Final") gerendert. Der Owner bezieht sich auf den Live-Zustand der
    // Bewertung (wird NICHT mit einem Snapshot eingefroren) und ist änderbar, solange die Bewertung
    // nicht final ist. Finalisierte Bewertungen zeigen stattdessen direkt einen UserLink (siehe PanelRevaOrder).
    //
    // Owner-Optionen: Pool (empty_owner) und die Selbst-Zuweisung ("mir zuweisen") brauchen kein
    // reva-ownership-assign (Pool/Self sind im Backend rechtfrei, siehe LifeCycleGuards ASSIGN — die
    // Revision-Rolle wird dort vorrangig geblockt; PanelRevaOrder blendet den Dropdown für sie komplett
    // aus). Das Recht wird nur für die Zuweisung an eine ANDERE Person gebraucht — nur dann wird die
    // (fremde) User-Liste geladen (siehe Effect oben) und hier zusätzlich angeboten.
    // In PreFinal/Correction darf nur Owner werden, wer abschließen darf (#19302 Z-B2): „mir“ nur mit
    // Abschlussrecht, die fremde Liste filtert das Backend (/assignableUsers) nach demselben Zustand.
    // Der Pool bleibt immer wählbar.
    const options = [empty_owner];
    const mayOwnInState = !isOwnershipRestrictedToFinalizers(lifeCycleState) || mayFinalize;
    if (ownUsername && mayOwnInState) options.push({label: ownFullName ?? ownUsername, value: ownUsername});
    if (revaOwnershipAssign)
        assignableOwners?.forEach((owner) => {
            if (owner.username === ownUsername) return; // sich selbst nur über die Zeile oben (inkl. Zustandsregel)
            options.push({label: owner.fullName ?? owner.username, value: owner.username});
        });
    // Der aktuelle (ggf. fremde) Owner muss als Option vorhanden sein, damit das Dropdown seinen Namen
    // anzeigen kann — auch ohne Zuweisungsrecht (dann rein zur Anzeige; wechseln kann man nur zu Pool/Self).
    if (currentOwner && !options.some((o) => o.value === currentOwner))
        options.push({label: currentOwnerFullName ?? currentOwner, value: currentOwner});
    const copyLink = async () => {
        await navigator.clipboard.writeText(`${window.location.origin}/object/overview?id=${revaId}`);
        toastRef.current?.show({
            severity: "success",
            summary: "Success",
            detail: t("order.owner.copy_link_success"),
        });
    };

    const confirmHeader = (
        <div className={"flex flex-row"} style={{alignItems: "center"}}>
            <div className={"flex-1 flex-grow"}>{t("order.owner.change_confirmation_title")}</div>
            <span
                id={"copyLink"}
                className="pi pi-copy text-primary mr-2 cursor-pointer"
                onClick={() => void copyLink()}
            />
            <Tooltip target="#copyLink" content={t("order.owner.copy_link_tooltip")} />
        </div>
    );
    const confirmMessage = (
        <>
            <p>{t("order.owner.change_confirmation_question")}</p>
            <p>{t("order.owner.change_confirmation_note")}</p>
        </>
    );

    // Toast und Rücksprung kommen nur aus der Backend-Notification in performAssignment — im Fehlerfall
    // bleibt der Nutzer mit der Fehlermeldung in der Bewertung (#19302 Z-B1).
    const onConfirmAccept = () => {
        if (pendingUser) void performAssignment(pendingUser);
    };

    return (
        <div className={"mb-0"}>
            <ConfirmDialog
                visible={showConfirmDialog}
                onHide={() => setShowConfirmDialog(false)}
                header={confirmHeader}
                message={confirmMessage}
                icon="pi pi-exclamation-triangle icon-warning"
                accept={onConfirmAccept}
                reject={() => setShowConfirmDialog(false)}
            />
            <Dropdown
                size={1}
                tabIndex={1}
                className={className}
                placeholder={t("order.owner.empty")}
                value={assigning ? optimisticOwner : currentOwner}
                options={options}
                onChange={onChange}
                disabled={assigning}
                loading={assigning}
            />
            <Toast ref={toastRef} position="top-right" />
        </div>
    );
}
